import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  categories: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    setCategories: (state, action) => {
      state.categories = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setCategories, setTotalCount, setTotalPages, setLoading, setError } = categorySlice.actions;

// Get categories list (paginated & searchable)
export const getCategories = (filters = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';

    const params = { page, limit, search };

    const response = await axiosInstance.get('/admin/category/list', { params });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || response.data.data || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setCategories(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setCategories([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch categories';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setCategories([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch categories';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single category details
export const getCategoryDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/category/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch category details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch category details';
    return { status: false, message: errorMsg };
  }
};

// Add new category
export const addCategory = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/category/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add category' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add category';
    return { status: false, message: errorMsg };
  }
};

// Update category
export const updateCategory = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/category/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update category' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update category';
    return { status: false, message: errorMsg };
  }
};

// Delete category
export const deleteCategory = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/category/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete category' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete category';
    return { status: false, message: errorMsg };
  }
};

export default categorySlice.reducer;
